import { useState } from 'react';
import { WebhookTester } from './components/WebhookTester';
import { ApiTester } from './components/ApiTester';
import { AdPlacement } from './components/AdPlacement';

type Tab = 'api' | 'webhook';

export function App() {
  const [activeTab, setActiveTab] = useState<Tab>('api');

  return (
    <div className={`min-h-screen ${activeTab === 'api' ? 'theme-api' : 'theme-webhook'}`}>
      {/* Header */}
      <header className="border-b fixed top-0 left-0 right-0 z-50 bg-hacker-bg" style={{ borderColor: 'var(--theme-color)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
            <div className="flex items-center">
              <span className="terminal-prompt" style={{ color: 'var(--theme-color)' }}>$</span>
              <h1 className="text-base sm:text-lg md:text-xl font-mono ml-2 break-all">
                <span style={{ color: 'var(--theme-color)' }}>const</span>{' '}
                <span className="text-hacker-text">{activeTab === 'api' ? 'apiTester' : 'webhookTester'}</span>{' '}
                <span style={{ color: 'var(--theme-color)' }}>=</span>{' '}
                <span style={{ color: 'var(--theme-color)' }}>new</span>{' '}
                <span className="text-hacker-text">TestHook</span>
                <span style={{ color: 'var(--theme-color)' }}>()</span>;
              </h1>
            </div>
            <nav className="flex space-x-2 sm:space-x-4 w-full sm:w-auto">
              <button
                onClick={() => setActiveTab('api')}
                className={`px-2 sm:px-3 py-2 rounded text-sm font-mono transition-all duration-300 border-1 flex-1 sm:flex-none ${
                  activeTab === 'api'
                    ? 'border-[var(--theme-color)] text-[var(--theme-color)] shadow-[var(--theme-shadow)]'
                    : 'border-transparent text-hacker-text-muted hover:text-hacker-text hover:border-[var(--theme-color)]'
                }`}
              >
                API Tester
              </button>
              <button
                onClick={() => setActiveTab('webhook')}
                className={`px-2 sm:px-3 py-2 rounded text-sm font-mono transition-all duration-300 border-1 flex-1 sm:flex-none ${
                  activeTab === 'webhook'
                    ? 'border-[var(--theme-color)] text-[var(--theme-color)] shadow-[var(--theme-shadow)]'
                    : 'border-transparent text-hacker-text-muted hover:text-hacker-text hover:border-[var(--theme-color)]'
                }`}
              >
                Webhook Tester
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-32 sm:mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative">
          <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>
          <div className="lg:col-span-2 h-full relative z-10">
            <div className="h-full">
              {activeTab === 'api' ? <ApiTester /> : <WebhookTester />}
            </div>
          </div>
          <div className="space-y-8 h-full relative z-10">
            <AdPlacement />
            
            {/* About Developer Section */}
            <div className="webhook-section p-4">
              <div className="terminal-header">
                <h3 className="text-sm font-mono" style={{ color: 'var(--theme-color)' }}>About Developer</h3>
              </div>
              <div className="text-hacker-text-muted text-xs mt-3">
                <p>Hi, I'm Okuobong, a passionate full-stack developer focused on building innovative solutions and contributing to the developer ecosystem.</p>
                <div className="mt-4 flex justify-center space-x-6">
                  <a
                    href="https://okuobong.vercel.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-hacker-text-muted hover:text-neon-api transition-colors"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1 16.057V7.943L18 12l-7 4.057z"/>
                    </svg>
                  </a>
                  <a
                    href="https://github.com/okuobongntuk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-hacker-text-muted hover:text-neon-api transition-colors"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </a>
                  <a
                    href="https://linkedin.com/in/okuobongntuk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-hacker-text-muted hover:text-neon-api transition-colors"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  <a
                    href="https://facebook.com/okuobongntuk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-hacker-text-muted hover:text-neon-api transition-colors"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Copyright Section */}
            <div className="webhook-section p-4">
              <div className="terminal-header">
                <h3 className="text-sm font-mono" style={{ color: 'var(--theme-color)' }}>Copyright</h3>
              </div>
              <div className="text-hacker-text-muted text-xs mt-3">
                <p>© {new Date().getFullYear()} Oku-obong Ntuk</p>
                <p className="mt-1">MIT License</p>
                <p className="mt-1 flex items-center">
                  Built with 
                  <span className="mx-1" role="img" aria-label="love">❤️</span> 
                  from 
                  <span className="mx-1" role="img" aria-label="Nigeria">🇳🇬</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App; 